import { useEffect, useRef, useState } from "react";
import type { ProductInfoCreative } from "@/feature/ads/cretives/types/creatives.types";
import { useAuth } from "@/context/AuthContext";
import type { Language } from "@/utils/dictionary";
import { Step1SelectProduct } from "@/feature/ads/cretives/components/modals/steps/Step1SelectProduct";
import { Step2VideoStudio } from "./Step2VideoStudio";
import { useVideoDraftStore } from "@/store/videoDraftStore";

type Step = 1 | 2;

interface CreateVideoModalProps {
  open: boolean;
  onClose: () => void;
  initialProduct?: ProductInfoCreative | null;
}

export function CreateVideoModal({ open, onClose, initialProduct }: CreateVideoModalProps) {
  const modalRef = useRef<HTMLDivElement | null>(null);
  const { language } = useAuth();
  const [step, setStep] = useState<Step>(initialProduct ? 2 : 1);
  const [selectedProduct, setSelectedProduct] = useState<ProductInfoCreative | null>(initialProduct ?? null);

  // Phase 2 fix: bloquear cierre por click-outside cuando hay un draft activo.
  // Sin esto, un click accidental fuera del modal cerraba el preview Y reseteaba
  // el store, perdiendo el reference_id y obligando a regenerar (otros 194 créditos).
  // El usuario solo puede salir del preview con los botones explícitos
  // "Cancelar y devolver créditos" o "Aprobar y generar video" (o el X del header).
  const draftStoreStatus = useVideoDraftStore((s) => s.storeStatus);
  const isDraftActive =
    draftStoreStatus === "directing" ||
    draftStoreStatus === "ready" ||
    draftStoreStatus === "approving" ||
    draftStoreStatus === "rejecting" ||
    draftStoreStatus === "editing_script" ||
    draftStoreStatus === "regenerating_image" ||
    draftStoreStatus === "regenerating_style" ||
    draftStoreStatus === "rendering" ||
    draftStoreStatus === "finished";

  useEffect(() => {
    if (!open) return;
    if (initialProduct) {
      setStep(2);
      setSelectedProduct(initialProduct);
    } else {
      setStep(1);
      setSelectedProduct(null);
    }
  }, [open, initialProduct]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // While a draft is in flight or waiting for approval, ignore outside clicks.
      // The user must use the explicit Cancel / Approve buttons (or the X) so
      // they can't accidentally lose 194 credits worth of progress.
      if (isDraftActive) return;

      const target = event.target as Node;
      if (!modalRef.current?.contains(target)) {
        const isInsideNestedModal =
          target instanceof Element && target.closest("[data-fluxi-nested-modal]");
        if (!isInsideNestedModal) {
          onClose();
        }
      }
    };

    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open, onClose, isDraftActive]);

  if (!open) return null;

  // Phase 5.4: cuando el draft está corriendo (director Gemini o regenerate
  // style), achicamos el modal entero para no mostrar una pantalla gigante
  // con un loader chiquito en el medio. Mismo patrón que el onboarding del
  // funnel cuando está procesando.
  const isLoadingDraft =
    step === 2 &&
    (draftStoreStatus === "directing" ||
      draftStoreStatus === "regenerating_style");

  // Estilos inline para tener interpolación suave entre tamaños. Tailwind
  // max-w-* no anima entre clases distintas, así que usamos width directo.
  // IMPORTANTE: ambos states usan height fijo en px — CSS no puede animar
  // entre `auto` y un valor concreto, y eso causaba el "salto" de la
  // transición entre el loader y el preview.
  const sizeStyle: React.CSSProperties =
    step === 2 && !isLoadingDraft
      ? {
          width: "min(95vw, 1024px)",
          height: "min(90vh, 860px)",
        }
      : step === 2 && isLoadingDraft
      ? {
          // Loading state del draft: height fijo para que la transición
          // a la vista grande del preview pueda interpolar (CSS no anima
          // entre auto y px).
          width: "min(95vw, 480px)",
          height: "min(90vh, 420px)",
        }
      : {
          // Step 1 (selección de producto): height auto, el contenido
          // decide su propio alto.
          width: "min(95vw, 480px)",
          height: "auto",
        };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />

      <div
        ref={modalRef}
        className="relative bg-background rounded-2xl shadow-2xl overflow-hidden border border-border/50 transition-[width,height] duration-500 ease-out"
        style={sizeStyle}
      >
        {step === 1 && (
          <Step1SelectProduct
            language={language as Language}
            onClose={onClose}
            onSelectProduct={(product) => {
              setSelectedProduct(product);
              setStep(2);
            }}
          />
        )}
        {step === 2 && selectedProduct && (
          <Step2VideoStudio
            product={selectedProduct}
            onClose={onClose}
          />
        )}
      </div>
    </div>
  );
}
